import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import {CLStore} from 'common/src/redux/store';
import {z} from 'zod';
import {CreativeFormat} from '../../../../creatives.types';
import getCreativeById from '../../../utils/getCreativeById';
import {CreativesManagerAgentToolName} from '../creativesManagerAgent.enums';

const inputSchema = z.object({
  creativeId: z.number(),
  includeName: z.boolean(),
  includeType: z.boolean(),
  includeTemplate: z.boolean(),
  includeFormats: z.boolean(),
  includeCreateTime: z.boolean(),
  includeUpdateTime: z.boolean(),
  includeCreatedByUserName: z.boolean(),
  includeUpdatedByUserName: z.boolean(),
  includeFeatures: z.boolean(),
  includeUsedInCampaigns: z.boolean(),
});

function getCreativeInfoTool(store: CLStore) {
  async function execute({
    input: {
      creativeId,
      includeName = false,
      includeType = false,
      includeTemplate = false,
      includeFormats = false,
      includeCreateTime = false,
      includeUpdateTime = false,
      includeCreatedByUserName = false,
      includeUpdatedByUserName = false,
      includeFeatures = false,
      includeUsedInCampaigns = false,
    },
  }: ToolExecuteParams<typeof inputSchema>): Promise<ToolResult> {
    const creative = await getCreativeById(creativeId, store);

    if (!creative) {
      return toolError(`Creative with ID ${creativeId} not found.`);
    }

    const {
      id,
      name,
      type,
      template,
      formats,
      createTime,
      updateTime,
      createdByUserName,
      updatedByUserName,
      features,
      usedInCampaigns,
    } = creative;

    const creativeMetadata = {
      id,
      ...(includeName && {name}),
      ...(includeType && {type: type.uiName}),
      ...(includeTemplate && {
        template: {
          id: template.id,
          name: template.name,
          type: template.type,
        },
      }),
      ...(includeFormats && {
        formats: formats.map((format: CreativeFormat) => ({
          size: `${format.width}x${format.height}`,
        })),
      }),
      ...(includeCreateTime && {createTime}),
      ...(includeUpdateTime && {updateTime}),
      ...(includeCreatedByUserName && {createdByUserName}),
      ...(includeUpdatedByUserName && {updatedByUserName}),
      ...(includeFeatures && {features}),
      ...(includeUsedInCampaigns && {usedInCampaigns}),
    };

    return toolResult(JSON.stringify(creativeMetadata));
  }

  return tool({
    name: CreativesManagerAgentToolName.GetCreativeInfo,
    description: 'Get detailed information about a specific creative by its ID',
    displayName: 'Getting creative metadata',
    execute,
    inputSchema,
  });
}

export default getCreativeInfoTool;
